import {
  Component,
  Host,
  h,
  Prop,
  Event,
  Element,
  State,
  EventEmitter,
  Watch
} from "@stencil/core";

// Other Libraries
import {
  TreeViewItemModel,
  TreeViewItemModelExtended
} from "@genexus/chameleon-controls-library/dist/types/components/tree-view/types";
import { MercuryBundles } from "@genexus/mercury";

import { createArrayOfItems } from "./helpers";

// Custom Imports
import { Locale } from "../../common/locale";
import { KB_OBJECT } from "./helpers";
import {
  ComboBoxItemModel,
  ComboBoxModel
} from "@genexus/chameleon-controls-library/dist/types/components/combo-box/types";
import { ChComboBoxRenderCustomEvent } from "@genexus/chameleon-controls-library";
import { ObjectBaseData } from "../../common/types";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form",
  "utils/layout",
  "utils/typography",
  "components/button",
  "components/checkbox",
  "components/edit",
  "components/tree-view",
  "components/combo-box",
  "components/icon",
  "utils/spacing"
];

const CHECKBOX_CHECKED_VALUE = "true";
const CHECKBOX_UNCHECKED_VALUE = "false";

@Component({
  tag: "gx-ide-deployment-tool",
  styleUrl: "deployment-tool.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/deployment-tool"]
})
export class GxIdeDeploymentTool {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  #renderedFirstTime = false;
  #flattenedObjectTypeData: Map<string, ObjectTypeData> = new Map();
  #currentDeploymentUnitId: string | undefined;

  @Element() el: HTMLGxIdeDeploymentToolElement;
  #onlyPackageCheckboxEl!: HTMLChCheckboxElement;
  #targetTypesEl!: HTMLChComboBoxRenderElement;
  #applicationServerEl!: HTMLChComboBoxRenderElement;
  #generatorEl!: HTMLChComboBoxRenderElement;
  #deploymentUnitsEl!: HTMLChComboBoxRenderElement;
  #displayNameEl!: HTMLChEditElement;

  @State() checkedObjectsIds: string[] = [];
  @State() noObjects: boolean;
  @State() selectedDeployTargetData: DeployTargetData = null;
  @State() addedObjects: TypedObjectData[] = [];
  @State() allObjectTypes: ObjectTypeData[];
  @State() treeModel: TreeViewItemModel[] = [];
  @State() allowDeploy: boolean = true;
  @Watch("treeModel")
  watchTreeModelHandler(newState: TreeViewItemModel[]) {
    this.noObjects = !!(newState.length === 0 || !newState);
  }

  /**
   * Available deployment units list
   */
  @Prop() readonly deploymentUnits: ObjectBaseData[];

  /**
   * Available target types list
   */
  @Prop() readonly targetTypes: DeployTargetData[];

  /**
   * Defines the default target type (will be selected by default)
   */
  @Prop() readonly defaultTargetTypeId?: string;

  /**
   * Defines id for new deployment unit option
   */
  @Prop() readonly newDeploymentUnitId: string;

  /**
   * Array of possible object types
   */
  @Prop() readonly objectTypes: ObjectTypeData[];
  @Watch("objectTypes")
  watchObjectTypesHandler(newState: ObjectTypeData[]) {
    this.#flattenObjectTypes(newState);
  }

  /**
   * Callback invoked when the user wants to access the export options.
   */
  // eslint-disable-next-line @stencil-community/decorators-style
  @Prop()
  readonly getDeploymentUnitObjectsCallback: GetDeploymentUnitObjectsCallback;

  /**
   * Callback invoked when the user wants to access the export options.
   */
  @Prop() readonly optionsCallback: OptionsCallback;

  /**
   * Callback invoked when user wants to start deploy process
   */
  @Prop() readonly deployCallback: DeployCallback;

  /**
   * Callback invoked when the user wants to add objects.
   */
  @Prop() readonly addObjectsCallback: AddObjectsCallback;

  /**
   * Callback invoked when the user wants to create a deployment unit.
   */
  @Prop() readonly createDeploymentUnitCallback: CreateDeploymentUnitCallback;

  /**
   * This event is emitted once just after the component is fully loaded and the first render() occurs.
   */
  @Event() componentDidLoadEvent: EventEmitter<boolean>;

  /**
   * This event is emitted once just after the component is fully loaded and the first render() occurs
   */
  @Event() componentDidRenderFirstTime: EventEmitter<boolean>;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.selectedDeployTargetData = this.#getInitialSelectedTarget();
    this.allObjectTypes = [...this.objectTypes];
    this.#evaluateObjects();
    this.#flattenObjectTypes(this.allObjectTypes);
    if (this.deploymentUnits.length > 0) {
      this.#loadDeploymentUnits(this.deploymentUnits[0].id);

      if (this.targetTypes.length > 0 && this.optionsCallback) {
        this.optionsCallback(
          this.deploymentUnits[0].id,
          this.targetTypes[0].id
        );
      }
    }
  }

  componentDidRender() {
    if (!this.#renderedFirstTime) {
      this.componentDidRenderFirstTime.emit(
        this.#componentLocale.componentName
      );
      this.#renderedFirstTime = true;
    }
  }

  #getInitialSelectedTarget = () => {
    if (this.defaultTargetTypeId) {
      const target = this.targetTypes.find(
        itTarget => itTarget.id === this.defaultTargetTypeId
      );
      if (target) {
        return target;
      }
    }
    return this.targetTypes[0];
  };

  #evaluateObjects = () => {
    this.noObjects = !!(this.treeModel?.length === 0 || !this.treeModel);
  };

  #handleGetDeploymentUnitObjectsCallback = async (ev: any) => {
    await this.#loadDeploymentUnits(ev.detail);
    this.#optionsCallbackHandler();
  };

  #loadDeploymentUnits = async (id: string) => {
    if (id === this.newDeploymentUnitId) {
      // New deployment unit should be created before executing the deploy action.
      return;
    }

    this.#currentDeploymentUnitId = id;

    const objectData = await this.getDeploymentUnitObjectsCallback(id);
    this.addedObjects = [...objectData];
    this.treeModel = createArrayOfItems(this.addedObjects, this.allObjectTypes);
  };

  #handleCreateDeploymentUnit = async () => {
    if (this.createDeploymentUnitCallback) {
      const deploymentUnitData = await this.createDeploymentUnitCallback();
      if (!deploymentUnitData) {
        return;
      }

      if (!this.deploymentUnits.find(du => du.id === deploymentUnitData.id)) {
        // Update deployment units:
        this.deploymentUnits.unshift(deploymentUnitData);

        // Update combo:
        const deploymentUnitComboBoxItem =
          this.#getComboModelItem(deploymentUnitData);

        const updatedDeploymentUnitsModel = [
          deploymentUnitComboBoxItem,
          ...this.#deploymentUnitsEl.model
        ];
        this.#deploymentUnitsEl.model = updatedDeploymentUnitsModel;
      }

      this.#deploymentUnitsEl.value = deploymentUnitData.id;

      // Trigger selection callback:
      await this.#loadDeploymentUnits(deploymentUnitData.id);
    }
  };

  #optionsCallbackHandler = () => {
    if (this.optionsCallback) {
      this.optionsCallback(
        this.#deploymentUnitsEl.value,
        this.#targetTypesEl.value
      );
    }
  };

  #addNewTypesToModel = () => {
    const allTypes: ObjectTypeData[] = [...this.allObjectTypes];

    this.addedObjects.forEach(addedObject => {
      const matchingType = this.allObjectTypes.find(
        objectType => objectType.id === addedObject.type
      );

      if (!matchingType) {
        allTypes.push({
          id: addedObject.type,
          name: addedObject.type,
          icon: ""
        });
      }
    });
    this.allObjectTypes = [...allTypes];
  };

  #addObjectsCallbackHandler = async () => {
    if (this.addObjectsCallback) {
      const objects: TypedObjectData[] = await this.addObjectsCallback();
      this.addedObjects = [...objects, ...this.addedObjects];

      this.#addNewTypesToModel();
      this.treeModel = createArrayOfItems(
        this.addedObjects,
        this.allObjectTypes
      );
    }
  };

  #deployCallbackHandler = async () => {
    if (this.#deploymentUnitsEl.value === this.newDeploymentUnitId) {
      await this.#handleCreateDeploymentUnit();
    }

    const data: DeployData = {
      targetId: this.#targetTypesEl.value,
      applicationServerId: this.#getApplicationServer(),
      displayName: this.#displayNameEl?.value,
      generatorId: this.#generatorEl?.value,
      onlyPackage: this.#onlyPackageCheckboxEl
        ? this.#onlyPackageCheckboxEl.value === CHECKBOX_CHECKED_VALUE
        : undefined,
      objects: this.checkedObjectsIds
    };

    if (this.deployCallback) {
      this.allowDeploy = false;
      await this.deployCallback(data);
      this.allowDeploy = true;
    }
  };

  #getApplicationServer = () => {
    if (this.#applicationServerEl?.value) {
      return this.#applicationServerEl.value;
    }
    return this.selectedDeployTargetData?.applicationServers.length > 0
      ? this.selectedDeployTargetData?.applicationServers[0].id
      : undefined;
  };

  #handleTargetTypesChange = (ev: any) => {
    const selectedId = ev.detail;

    this.selectedDeployTargetData =
      this.targetTypes.find(targetType => targetType.id === selectedId) || null;

    if (
      !this.selectedDeployTargetData.canCreatePackage &&
      this.#onlyPackageCheckboxEl.value === CHECKBOX_CHECKED_VALUE
    ) {
      this.#onlyPackageCheckboxEl.value = CHECKBOX_UNCHECKED_VALUE;
    }

    this.#optionsCallbackHandler();
  };

  #flattenObjectTypes(newState: ObjectTypeData[]) {
    newState.forEach(objectType => {
      this.#flattenedObjectTypeData.set(objectType.id, objectType);
    });
  }

  #objectsTreeCheckedItemsChangedHandler = (
    event: CustomEvent<Map<string, TreeViewItemModelExtended>>
  ) => {
    const allItemsWithCheckbox = [...event.detail.values()];

    this.checkedObjectsIds = allItemsWithCheckbox
      .filter(
        checkedItem =>
          checkedItem.item.checked && checkedItem.item.metadata === KB_OBJECT
      )
      .map(checkedItem => checkedItem.item.id);
  };

  #evaluateContentToDisplay = () => {
    if (this.noObjects) {
      return (
        <gx-ide-empty-state
          isAnimated={false}
          stateTitle={this.#componentLocale.main.noObjectsToDisplay}
          key="no-objects-empty-state"
          stateIconSrc="objects/object"
        ></gx-ide-empty-state>
      );
    }

    return (
      <ch-tree-view-render
        class="tree-view"
        model={this.treeModel}
        dragDisabled={true}
        dropDisabled={true}
        toggleCheckboxes={true}
        showLines="last"
        checked={true}
        checkbox={true}
        onCheckedItemsChange={this.#objectsTreeCheckedItemsChangedHandler}
      />
    );
  };

  #getComboModel = (baseCollection: ObjectBaseData[]): ComboBoxModel => {
    return baseCollection.map(obj => this.#getComboModelItem(obj));
  };

  #getComboModelItem = (
    baseCollectionItem: ObjectBaseData
  ): ComboBoxItemModel => {
    return {
      caption: baseCollectionItem.name,
      value: baseCollectionItem.id
    };
  };

  #renderComboSelector = (
    properties: ComboSelectorProperties,
    withField: boolean = false
  ) => {
    const controlId = properties.label.replace(/ /g, "-").toLowerCase();
    const labelClasses = `label ${controlId}`;
    const comboBoxClasses = properties.class
      ? `combo-box ${properties.class}`
      : "combo-box";
    const labelAndComboBox = [
      <label class={labelClasses} htmlFor={controlId}>
        {properties.label}
      </label>,
      <ch-combo-box-render
        class={comboBoxClasses}
        value={
          properties.value ? properties.value : properties.baseModel[0]?.id
        }
        model={this.#getComboModel(properties.baseModel)}
        id={controlId}
        ref={properties.refCallback}
        onInput={properties.onInputCallback}
      />
    ];
    return withField ? (
      <div class="field field-inline">{labelAndComboBox}</div>
    ) : (
      labelAndComboBox
    );
  };

  #renderHeader = () => {
    return (
      <div slot="header" class="header dialog-header-with-border">
        {this.#renderComboSelector(
          {
            id: "deploymentUnits",
            baseModel: this.deploymentUnits,
            label: this.#componentLocale.header.objectsToDeploy,
            value: this.#currentDeploymentUnitId,
            refCallback: (el: HTMLChComboBoxRenderElement) => {
              this.#deploymentUnitsEl = el as HTMLChComboBoxRenderElement;
            },
            onInputCallback: this.#handleGetDeploymentUnitObjectsCallback
          },
          true
        )}

        <button
          class="button-primary"
          onClick={this.#addObjectsCallbackHandler}
        >
          {this.#componentLocale.main.addButton}
        </button>
      </div>
    );
  };

  #renderApplicationServerSelector = () => {
    return this.#renderComboSelector({
      id: "application-servers",
      class: "application-server-combo",
      baseModel: this.selectedDeployTargetData?.applicationServers,
      label: this.#componentLocale.main.applicationServer,
      refCallback: (el: HTMLChComboBoxRenderElement) => {
        this.#applicationServerEl = el as HTMLChComboBoxRenderElement;
      }
    });
  };

  #renderGeneratorSelector = () => {
    return this.#renderComboSelector({
      id: "generator",
      class: "generator-combo",
      baseModel: this.selectedDeployTargetData?.generators,
      label: this.#componentLocale.main.generator,
      refCallback: (el: HTMLChComboBoxRenderElement) => {
        this.#generatorEl = el as HTMLChComboBoxRenderElement;
      }
    });
  };

  #renderTargetSelector = () => {
    return [
      this.#renderComboSelector({
        id: "targetTypes",
        class: "target-types-combo",
        baseModel: this.targetTypes,
        label: this.#componentLocale.main.targetTypes,
        value: this.selectedDeployTargetData
          ? this.selectedDeployTargetData.id
          : this.defaultTargetTypeId,
        refCallback: (el: HTMLChComboBoxRenderElement) => {
          this.#targetTypesEl = el as HTMLChComboBoxRenderElement;
        },
        onInputCallback: this.#handleTargetTypesChange
      }),
      <div class="target-types-button-checkbox-wrapper buttons-spacer">
        <button
          class="button-tertiary button-icon-only"
          onClick={this.#optionsCallbackHandler}
        >
          <ch-image class="icon-m" src="system/settings/primary" />
        </button>
        <ch-checkbox
          disabled={!this.selectedDeployTargetData?.canCreatePackage}
          class="checkbox"
          caption={this.#componentLocale.main.onlyPackageLabel}
          part="only-package-checkbox"
          checkedValue={CHECKBOX_CHECKED_VALUE}
          unCheckedValue={CHECKBOX_UNCHECKED_VALUE}
          value={CHECKBOX_UNCHECKED_VALUE}
          ref={(el: HTMLChCheckboxElement) =>
            (this.#onlyPackageCheckboxEl = el as HTMLChCheckboxElement)
          }
        />
      </div>
    ];
  };

  #renderDisplayNameInput = () => {
    return [
      <label class="label display-name" htmlFor="display-name">
        {this.#componentLocale.main.displayName}
      </label>,
      <ch-edit
        class="input display-name-input"
        id="display-name"
        ref={(el: HTMLChEditElement) => {
          if (el) {
            this.#displayNameEl = el;
          }
        }}
      />
    ];
  };

  #renderFooter = () => {
    return (
      <div slot="footer-end" class="footer dialog-footer-with-border">
        <button
          class="button-primary"
          onClick={this.#deployCallbackHandler}
          disabled={!this.allowDeploy}
        >
          {this.#componentLocale.main.deployButton}
        </button>
      </div>
    );
  };

  render(): void {
    return (
      <Host class="widget spacing-body">
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        {this.#renderHeader()}

        {this.#evaluateContentToDisplay()}
        <div class="deploy-properties field-group">
          {this.#renderTargetSelector()}

          {this.selectedDeployTargetData?.applicationServers?.length > 1 &&
          this.selectedDeployTargetData.canSelectApplicationServer
            ? this.#renderApplicationServerSelector()
            : []}

          {this.selectedDeployTargetData?.generators?.length > 0
            ? this.#renderGeneratorSelector()
            : []}

          {this.selectedDeployTargetData?.description && (
            <p class="body-italic-s target-type__description">
              {this.selectedDeployTargetData?.description}
            </p>
          )}

          {this.selectedDeployTargetData?.canDefineDisplayName
            ? this.#renderDisplayNameInput()
            : []}
        </div>

        {this.#renderFooter()}
      </Host>
    );
  }
}

type ComboSelectorProperties = {
  class?: string;
  id: string;
  baseModel: ObjectBaseData[];
  value?: string;
  label: string;
  refCallback: (el: HTMLChComboBoxRenderElement) => void;
  onInputCallback?: ((event: ChComboBoxRenderCustomEvent<string>) => void) &
    ((event: InputEvent) => void);
};

export type AddObjectsCallback = () => Promise<TypedObjectData[]>;
export type OptionsCallback = (
  delpoymentUnitId: string,
  targetId: string
) => Promise<void>;
export type GetDeploymentUnitObjectsCallback = (
  id: string
) => Promise<TypedObjectData[]>;

export type DeployCallback = (data: DeployData) => Promise<boolean>;

export type CreateDeploymentUnitCallback = () => Promise<
  ObjectBaseData | undefined
>;

export type ObjectTypeData = ObjectBaseData & {
  icon: string;
};

export type DeployTargetData = ObjectBaseData & {
  canCreatePackage: boolean;
  canDefineDisplayName: boolean;
  canSelectApplicationServer: boolean;
  applicationServers: ObjectBaseData[];
  generators: ObjectBaseData[];
  description: string;
};

export type TypedObjectData = ObjectBaseData & {
  type: string;
};

export type DeployData = {
  targetId: string;
  applicationServerId?: string;
  displayName?: string;
  generatorId?: string;
  onlyPackage: boolean;
  objects: string[];
};
